import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const generateSessionToken = (userRole = 'user') => {
    const token = {
      id: Math.random().toString(36).substr(2, 9),
      timestamp: Date.now(),
      expires: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
      role: userRole
    };
    return btoa(JSON.stringify(token));
  };

  const validateSessionToken = (token) => {
    try {
      const decoded = JSON.parse(atob(token));
      return {
        isValid: decoded.expires > Date.now(),
        role: decoded.role || 'user'
      };
    } catch (error) {
      return { isValid: false, role: 'user' };
    }
  };

  const hashPassword = async (password) => {
    const salt = process.env.REACT_APP_AUTH_SALT;
    if (!salt) {
      throw new Error('Authentication salt not configured');
    }
    
    const encoder = new TextEncoder();
    const data = encoder.encode(password + salt);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        const validation = validateSessionToken(token);
        if (validation.isValid) {
          setIsAuthenticated(true);
          setIsAdmin(validation.role === 'admin');
        } else {
          // Clear invalid token
          localStorage.removeItem('authToken');
          setIsAuthenticated(false);
          setIsAdmin(false);
        }
      }
      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = async (password) => {
    try {
      const hashedPassword = await hashPassword(password);
      
      if (process.env.REACT_APP_ADMIN_PASSWORD_HASH && 
          hashedPassword === process.env.REACT_APP_ADMIN_PASSWORD_HASH) {
        const token = generateSessionToken('admin');
        setIsAuthenticated(true);
        setIsAdmin(true);
        localStorage.setItem('authToken', token);
        return { success: true, isAdmin: true };
      }
      
      if (process.env.REACT_APP_USER_PASSWORD_HASH && 
          hashedPassword === process.env.REACT_APP_USER_PASSWORD_HASH) {
        const token = generateSessionToken('user');
        setIsAuthenticated(true);
        setIsAdmin(false);
        localStorage.setItem('authToken', token);
        return { success: true, isAdmin: false };
      }
      
      return { success: false, isAdmin: false };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, isAdmin: false };
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setIsAdmin(false);
    localStorage.removeItem('authToken');
  };

  // Auto-logout when token expires
  useEffect(() => {
    if (isAuthenticated) {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const decoded = JSON.parse(atob(token));
          const timeUntilExpiry = decoded.expires - Date.now();
          
          if (timeUntilExpiry > 0) {
            const timeoutId = setTimeout(() => {
              logout();
            }, timeUntilExpiry);
            
            return () => clearTimeout(timeoutId);
          } else {
            logout();
          }
        } catch (error) {
          logout();
        }
      }
    }
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isAdmin,
      isLoading, 
      login, 
      logout 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
